<template>
  <div
    :class="[
      'metric-carousel',
      isCarouselScrolling && 'metric-carousel--scrolling',
      resolvedRouteName === 'popular' && 'metric-carousel--scrolling-popular',
    ]"
  >
    <div class="metric-carousel__info">
      <div class="metric-carousel__copy-wrapper">
        <div>
          <h6 class="metric-carousel__title">{{ metric.name }} Courses</h6>
          <p class="metric-carousel__tagline">{{ taglines[metric.name] }}</p>
        </div>
        <router-link
          :class="[
            'metric-carousel__link',
            isCarouselScrolling
              ? 'metric-carousel__link--scrolling'
              : 'btn btn--secondary',
          ]"
          :to="{ name: 'allCourses', params: { name: resolvedRouteName } }"
        >
          Browse all
          <ChevronsRightIcon v-if="isCarouselScrolling" />
        </router-link>
      </div>
    </div>
    <Swiper
      class="metric-carousel__swiper"
      :parentClass="`metric-carousel__${resolvedRouteName}`"
      :swiperOptions="swiperOptions"
      :swiperReset="swiperReset"
      @slide-changed="onSlideChange"
    >
      <CourseTile
        v-for="course in sortedCourses"
        :key="course.objectID"
        :course="course"
        :height="tileHeight"
        :isFreeTrialUser="false"
        class="swiper-slide"
        :isDifficultyShown="true"
        :isInstructorPictureShown="true"
      />
    </Swiper>
    <div class="metric-carousel__link-wrapper">
      <router-link
        class="metric-carousel__link--bottom"
        :to="{ name: 'allCourses', params: { name: resolvedRouteName } }"
      >
        View all
      </router-link>
    </div>
  </div>
</template>

<script>
import { defineComponent, toRefs, ref, onMounted, computed } from 'vue'
import { duration } from '@/helpers'
import { createAlgoliaIndices } from '@/utils'
import CourseTile from '@/components/layout/CourseTile.vue'
import Swiper from '@/components/layout/Swiper.vue'
import { ChevronsRightIcon } from 'vue-feather-icons'

export default defineComponent({
  name: 'MetricCarousel',
  components: { CourseTile, Swiper, ChevronsRightIcon },
  props: {
    algoliaKeys: {
      type: Object,
      required: true,
    },
    metric: {
      type: Object,
      required: true,
    },
    isTablet: {
      type: Boolean,
      required: false,
    },
    isMobile: {
      type: Boolean,
      required: false,
    },
    activeCourses: {
      type: Array,
      required: false,
    },
  },
  setup(props) {
    const { algoliaKeys, metric } = toRefs(props)
    const courses = ref([])
    const taglines = ref({
      New: 'Get a first look at our latest courses as soon as they’re available.',
      Popular: 'Get to training with some fan-favorite courses.',
      Trending: 'The courses our students can’t stop raving about.',
    })
    const swiperOptions = ref({
      loop: false,
      mousewheel: {
        forceToAxis: true,
      },
      breakpoints: {
        0: {
          slidesPerView: 'auto',
          spaceBetween: 10,
        },
        500: {
          slidesPerView: 'auto',
          spaceBetween: 10,
        },
        768: {
          slidesPerView: 'auto',
          spaceBetween: 10,
        },
        1024: {
          slidesPerView: 'auto',
          spaceBetween: 10,
        },
        1300: {
          slidesPerView: 'auto',
          spaceBetween: 10,
        },
        1800: {
          slidesPerView: 'auto',
          spaceBetween: 10,
        },
      },
    })
    const swiperReset = ref(false)
    const isCarouselScrolling = ref(false)
    const isCarouselBeginning = ref(true)
    const onSlideChange = evt => {
      isCarouselBeginning.value = swiperReset.value ? false : evt.isBeginning
      if (
        isCarouselScrolling.value &&
        isCarouselBeginning.value &&
        !props.isMobile
      ) {
        isCarouselScrolling.value = false
        return
      }
      swiperReset.value = false
      if (!isCarouselScrolling.value && !props.isMobile) {
        isCarouselScrolling.value = true
        swiperReset.value = true
        isCarouselBeginning.value = false
      }
    }
    const handleResults = (_, { hits }) => (courses.value = hits)
    const createAlgoliaSearchInstance = () => {
      const {
        value: { appKey, appId, coursesIndex },
      } = algoliaKeys
      const setup = {
        keys: {
          appId,
          appKey,
        },
        contexts: [
          {
            name: metric.value.name,
            index: coursesIndex,
            facets: [metric.value.facet],
          },
        ],
        resultsHandler: handleResults,
      }
      createAlgoliaIndices(setup, {
        facet: metric.value.facet,
        value: metric.value.value,
      })
    }
    const mergedCourses = computed(() => {
      const mergeCourses = []
      courses.value.forEach(algoliaCourse => {
        props.activeCourses?.forEach(activeCourse => {
          if (
            algoliaCourse.objectID === activeCourse.id &&
            activeCourse.user_status?.last_activity_date
          ) {
            algoliaCourse.last_activity_date =
              activeCourse.user_status.last_activity_date
          }
        })
        algoliaCourse.duration_in_seconds = algoliaCourse.duration
          ? duration.convertToSeconds(algoliaCourse.duration)
          : null
        mergeCourses.push(algoliaCourse)
      })
      return mergeCourses
    })
    const sortedCourses = computed(() => {
      return mergedCourses.value.sort((a, b) => {
        if (metric.value.name === 'Popular') {
          return a.popular_order - b.popular_order
        }
        if (metric.value.name === 'Trending') {
          return a.trending_order - b.trending_order
        }
        return b.published_date - a.published_date
      })
    })

    const resolvedRouteName = computed(() => metric.value.name.toLowerCase())
    const tileHeight = computed(() => (props.isTablet ? 340 : 485))
    onMounted(() => {
      createAlgoliaSearchInstance()
    })

    return {
      mergedCourses,
      sortedCourses,
      taglines,
      resolvedRouteName,
      swiperOptions,
      tileHeight,
      isCarouselScrolling,
      onSlideChange,
      swiperReset,
      isCarouselBeginning,
    }
  },
})
</script>

<style lang="scss">
.metric-carousel {
  &__swiper {
    padding: 0 0 0 20px;
    margin: 0;
    flex: 1;
    mask-image: linear-gradient(
      to left,
      transparent 0%,
      #030303 10%,
      #030303 90%,
      transparent 100%
    );
    @include mobile {
      mask-image: linear-gradient(to left, transparent 0%, #030303 10%);
    }
  }
}
</style>
